import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Mail, X, Shield, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const inputCls = "w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary";

const ROLE_LABEL = { owner: "Owner", admin: "Admin", member: "Member" };

export default function MembersSettings() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const isAdminOrOwner = user?.role === "owner" || user?.role === "admin";
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [sending, setSending] = useState(false);
  const [entitlement, setEntitlement] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/organizations/me/members");
      setMembers(data.members || []);
      setInvites(data.invites || []);
    } catch { /* noop */ }
    try {
      const { data } = await api.get("/entitlement");
      setEntitlement(data);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/organizations/me/invites", { email, role });
      toast.success(`Invitation sent to ${email}.`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send invite.");
    } finally {
      setSending(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/organizations/me/invites/${id}`);
      setInvites((i) => i.filter((x) => x.invite_id !== id));
    } catch { toast.error("Could not revoke invite."); }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await api.patch(`/organizations/me/members/${userId}`, { role: newRole });
      toast.success("Role updated.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update role.");
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.email} from this workspace?`)) return;
    try {
      await api.delete(`/organizations/me/members/${m.user_id}`);
      toast.success("Member removed.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not remove member.");
    }
  };

  const memberLimit = entitlement?.limits?.members ?? null;
  const memberUsage = (entitlement?.usage?.members ?? members.length) + invites.length;
  const atMemberLimit = memberLimit !== -1 && memberLimit != null && memberUsage >= memberLimit;

  return (
    <div className="space-y-4" data-testid="members-settings">
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h2 className="font-display text-xl font-semibold tracking-tight">Team members</h2>
            <p className="mt-1 text-sm text-muted-foreground">Invite teammates to operate the business together.</p>
          </div>
          {entitlement && (
            <span className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground" data-testid="members-usage">
              {memberUsage} / {memberLimit === -1 ? "∞" : memberLimit} · {entitlement.plan}
            </span>
          )}
        </div>

        {isAdminOrOwner && (
          <form onSubmit={invite} className="mt-6 flex flex-col gap-3 sm:flex-row" data-testid="invite-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" className={inputCls} data-testid="invite-email-input" />
            <select value={role} onChange={(e) => setRole(e.target.value)} className={`${inputCls} sm:w-40`} data-testid="invite-role-select">
              <option value="member">Member</option>
              {isOwner && <option value="admin">Admin</option>}
            </select>
            <button type="submit" disabled={sending || atMemberLimit} data-testid="invite-submit-btn" className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background disabled:opacity-60">
              <UserPlus className="h-4 w-4" /> {sending ? "Sending…" : "Invite"}
            </button>
          </form>
        )}
        {atMemberLimit && (
          <p className="mt-3 text-xs text-brand-red" data-testid="members-limit-warning">
            Your plan allows {memberLimit} members. Upgrade to invite more.
          </p>
        )}

        <div className="mt-6 divide-y divide-border">
          {members.map((m) => {
            const isSelf = m.user_id === user?.user_id;
            const isMemberOwner = m.role === "owner";
            return (
              <div key={m.user_id} className="flex flex-wrap items-center gap-3 py-3" data-testid={`member-${m.user_id}`}>
                <Avatar className="h-9 w-9"><AvatarFallback className="brand-gradient-bg text-xs font-semibold text-white">{(m.name || m.email || "?").charAt(0).toUpperCase()}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{m.name || m.email} {isSelf && <span className="text-xs text-muted-foreground">(you)</span>}</p>
                  <p className="truncate text-xs text-muted-foreground">{m.email}</p>
                </div>
                {isOwner && !isMemberOwner && !isSelf ? (
                  <select
                    value={m.role || "member"}
                    onChange={(e) => changeRole(m.user_id, e.target.value)}
                    data-testid={`member-role-select-${m.user_id}`}
                    className="rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold outline-none focus:border-primary"
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold capitalize text-muted-foreground ring-1 ring-border">
                    {isMemberOwner && <Shield className="h-3 w-3" />}{ROLE_LABEL[m.role] || m.role}
                  </span>
                )}
                {isAdminOrOwner && !isMemberOwner && !isSelf && (
                  <button onClick={() => removeMember(m)} data-testid={`remove-member-${m.user_id}`} className="text-muted-foreground hover:text-destructive" title="Remove from workspace">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
        <h3 className="font-display text-base font-semibold tracking-tight">Pending invitations</h3>
        {invites.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">No pending invitations.</p>
        ) : (
          <div className="mt-4 divide-y divide-border">
            {invites.map((inv) => (
              <div key={inv.invite_id} className="flex items-center gap-3 py-3" data-testid={`invite-${inv.invite_id}`}>
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground"><Mail className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{inv.email}</p>
                  <p className="text-xs capitalize text-muted-foreground">{inv.role} · pending</p>
                </div>
                {isAdminOrOwner && (
                  <button onClick={() => revoke(inv.invite_id)} data-testid={`revoke-${inv.invite_id}`} className="text-muted-foreground hover:text-destructive" title="Revoke"><X className="h-4 w-4" /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
